import { useEffect, useRef, useState } from 'react';

/**
 * Unified refresh hook.
 *
 * Triggers the same refreshing state for both:
 *  1. Pull-to-refresh (touch drag from top of page past threshold).
 *  2. External "tab refresh" events dispatched on `window` with `eventName`
 *     (e.g. when the user re-taps the active bottom nav tab).
 *
 * Guarantees a single refresh animation at a time — additional triggers
 * while `refreshing` is true are ignored (no stacking).
 */
export function usePullToRefresh(eventName: string, options?: { threshold?: number; max?: number }) {
  const threshold = options?.threshold ?? 70;
  const max = options?.max ?? 110;
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);
  const refreshingRef = useRef(false);
  const selfDispatching = useRef(false);

  // Keep ref in sync so event listeners read the current value.
  useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);

  const beginRefresh = (dispatchEvent: boolean) => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    if (navigator.vibrate) navigator.vibrate(12);
    if (dispatchEvent) {
      selfDispatching.current = true;
      window.dispatchEvent(new Event(eventName));
      selfDispatching.current = false;
    }
    setTimeout(() => {
      refreshingRef.current = false;
      setRefreshing(false);
      setPull(0);
    }, 850);
  };

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0 || refreshingRef.current) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
      active.current = true;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!active.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setPull(0); return; }
      const eased = Math.min(max, dy * 0.55);
      setPull(eased);
    };
    const onTouchEnd = () => {
      if (!active.current) return;
      active.current = false;
      if (pull >= threshold) {
        beginRefresh(true);
      } else {
        setPull(0);
      }
      startY.current = null;
    };

    // External tab-refresh event (BottomNav re-tap, manual list refresh, etc.)
    const onExternalRefresh = () => {
      if (selfDispatching.current) return; // avoid feedback loop with our own dispatch
      window.scrollTo({ top: 0, behavior: 'smooth' });
      beginRefresh(false);
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener(eventName, onExternalRefresh);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener(eventName, onExternalRefresh);
    };
  }, [eventName, max, threshold, pull]);

  return { pull, refreshing, progress: Math.min(1, pull / threshold) };
}
